"use client";
import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

// Types for the questionnaire context
export interface QuestionnaireQuestion {
  id: string;
  question: string;
  answer: string;
  editable: boolean;
  questionFor: string;
  description: string;
}

export interface AuditQuestionnaireContextType {
  // Project identifiers
  siteId: string | null;
  vendorId: string | null;
  assessmentId: string | null;
  
  // Questions data
  questions: QuestionnaireQuestion[];
  
  // Error highlighting
  highlightedQuestions: string[];
  highlightedDescriptions: string[];
  
  // Actions
  setSiteId: (siteId: string | null) => void;
  setVendorId: (vendorId: string | null) => void;
  setAssessmentId: (assessmentId: string | null) => void;
  setQuestions: (questions: QuestionnaireQuestion[]) => void;
  updateQuestion: (questionId: string, updates: Partial<Omit<QuestionnaireQuestion, 'id'>>) => void;
  addQuestion: (question: QuestionnaireQuestion) => void;
  removeQuestion: (questionId: string) => void;
  clearAllData: () => void;
  
  // Error highlighting actions
  highlightQuestion: (questionId: string) => void;
  highlightDescription: (questionId: string) => void;
  clearQuestionHighlight: (questionId: string) => void;
  clearDescriptionHighlight: (questionId: string) => void;
  clearAllHighlights: () => void;
  
  // Computed values
  hasData: boolean;
  isComplete: boolean;
  hasHighlights: boolean;
  isQuestionHighlighted: (questionId: string) => boolean;
  isDescriptionHighlighted: (questionId: string) => boolean;
}

// Create the context
const AuditQuestionnaireContext = createContext<AuditQuestionnaireContextType | undefined>(undefined);

// Provider component
export const AuditQuestionnaireProvider = ({ children }: { children: ReactNode }) => {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [vendorId, setVendorId] = useState<string | null>(null);
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<QuestionnaireQuestion[]>([]);
  
  // Error highlighting state
  const [highlightedQuestions, setHighlightedQuestions] = useState<string[]>([]);
  const [highlightedDescriptions, setHighlightedDescriptions] = useState<string[]>([]);

  // Update a specific question
  const updateQuestion = useCallback((questionId: string, updates: Partial<Omit<QuestionnaireQuestion, 'id'>>) => {
    setQuestions(prevQuestions => 
      prevQuestions.map(question => 
        question.id === questionId 
          ? { ...question, ...updates }
          : question
      )
    );
  }, []);

  // Add a new question
  const addQuestion = useCallback((question: QuestionnaireQuestion) => {
    setQuestions(prevQuestions => [...prevQuestions, question]);
  }, []);

  // Remove a question
  const removeQuestion = useCallback((questionId: string) => {
    setQuestions(prevQuestions => 
      prevQuestions.filter(question => question.id !== questionId)
    );
  }, []);

  // Clear all data
  const clearAllData = useCallback(() => {
    setSiteId(null);
    setVendorId(null);
    setAssessmentId(null);
    setQuestions([]);
    setHighlightedQuestions([]);
    setHighlightedDescriptions([]);
  }, []);

  // Error highlighting actions
  const highlightQuestion = useCallback((questionId: string) => {
    setHighlightedQuestions(prev => 
      prev.includes(questionId) ? prev : [...prev, questionId]
    );
  }, []);

  const highlightDescription = useCallback((questionId: string) => {
    setHighlightedDescriptions(prev => 
      prev.includes(questionId) ? prev : [...prev, questionId]
    );
  }, []);

  const clearQuestionHighlight = useCallback((questionId: string) => {
    setHighlightedQuestions(prev => prev.filter(id => id !== questionId));
  }, []);

  const clearDescriptionHighlight = useCallback((questionId: string) => {
    setHighlightedDescriptions(prev => prev.filter(id => id !== questionId));
  }, []);

  const clearAllHighlights = useCallback(() => {
    setHighlightedQuestions([]);
    setHighlightedDescriptions([]);
  }, []);

  // Computed values
  const hasData = siteId !== null || vendorId !== null || assessmentId !== null || questions.length > 0;
  const isComplete = questions.length > 0 && questions.every(q => q.answer && q.answer.trim() !== '');
  const hasHighlights = highlightedQuestions.length > 0 || highlightedDescriptions.length > 0;
  const isQuestionHighlighted = useCallback((questionId: string) => 
    highlightedQuestions.includes(questionId), [highlightedQuestions]);
  const isDescriptionHighlighted = useCallback((questionId: string) => 
    highlightedDescriptions.includes(questionId), [highlightedDescriptions]);

  const contextValue: AuditQuestionnaireContextType = {
    // Project identifiers
    siteId,
    vendorId,
    assessmentId,
    
    // Questions data
    questions,
    
    // Error highlighting
    highlightedQuestions,
    highlightedDescriptions,
    
    // Actions
    setSiteId,
    setVendorId,
    setAssessmentId,
    setQuestions,
    updateQuestion,
    addQuestion,
    removeQuestion,
    clearAllData,
    
    // Error highlighting actions
    highlightQuestion,
    highlightDescription,
    clearQuestionHighlight,
    clearDescriptionHighlight,
    clearAllHighlights,
    
    // Computed values
    hasData,
    isComplete,
    hasHighlights,
    isQuestionHighlighted,
    isDescriptionHighlighted,
  };

  return (
    <AuditQuestionnaireContext.Provider value={contextValue}>
      {children}
    </AuditQuestionnaireContext.Provider>
  );
};

// Custom hook to use the context
export const useAuditQuestionnaireContext = () => {
  const context = useContext(AuditQuestionnaireContext);
  if (context === undefined) {
    throw new Error('useAuditQuestionnaireContext must be used within an AuditQuestionnaireProvider');
  }
  return context;
};

export default AuditQuestionnaireContext;
